import { supabase } from '@/integrations/supabase/client'

export interface AuditAction {
  action: string
  resource_type: string
  resource_id?: string
  details?: Record<string, any>
}

class AuditLogger {
  private async logAction(auditAction: AuditAction) {
    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.access_token) {
        console.warn('Cannot log audit action: No active session')
        return
      }

      // Call audit logging edge function
      const { error } = await supabase.functions.invoke('audit-logger', {
        body: auditAction,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        console.error('Audit logging failed:', error)
      }
    } catch (error) {
      console.error('Audit logging error:', error)
    }
  }

  // Authentication events
  async logLogin(method: 'password' | 'magic_link' = 'password') {
    await this.logAction({
      action: 'login',
      resource_type: 'auth',
      details: { method }
    })
  }

  async logLogout() {
    await this.logAction({
      action: 'logout',
      resource_type: 'auth'
    })
  }

  async logRegistration() {
    await this.logAction({
      action: 'registration',
      resource_type: 'auth'
    })
  }

  // Data access events
  async logDataAccess(resource_type: string, resource_id?: string, action: 'view' | 'create' | 'update' | 'delete' = 'view') {
    await this.logAction({
      action: `data_${action}`,
      resource_type,
      resource_id
    })
  }

  // Security events
  async logSecurityEvent(event_type: 'session_timeout' | 'rate_limit_exceeded' | 'invalid_access', details?: Record<string, any>) {
    await this.logAction({
      action: 'security_event',
      resource_type: 'security',
      details: { event_type, ...details }
    })
  }

  // Admin actions
  async logAdminAction(action: string, resource_type: string, resource_id?: string, details?: Record<string, any>) {
    await this.logAction({
      action: `admin_${action}`,
      resource_type,
      resource_id,
      details
    })
  }
}

export const auditLogger = new AuditLogger()